/**
 * Verfügbarkeit der Betreuungskräfte (Dispo): Verfügbarkeit / Urlaub.
 * „Kein Kontakt" ist seit 11.10.2026 nicht mehr wählbar; die Definition bleibt
 * nur, damit Altbestand weiter mit Farbe und Beschriftung angezeigt wird.
 *
 * Die Einträge liegen in `absences` (user_id = BK) und werden über die
 * bestehenden Abwesenheits-Endpunkte (`v1Absences`) angelegt, geändert und
 * gelöscht; der Grund ist einer der kanonischen Werte aus
 * BK_AVAILABILITY_TYPES. BK-übergreifend liefert sie
 *
 *   GET /v2/admin/bk-availability?type=&scope=&limit=
 *
 * für das Dispo-Widget der Startseite und den Filter der BK-Liste.
 */
import { apiV2 } from './api';
import type { V1User } from './v1';

export type BkAvailabilityType = 'abwesenheit' | 'verfuegbar' | 'urlaub' | 'kein_kontakt';

export interface BkAvailabilityTypeDef {
  type: BkAvailabilityType;
  /** In `absences.grund` gespeicherter Wert. */
  grund: string;
  labelKey: string;
  /** Farbe für Balken, Kennzeichen und Legende. */
  color: string;
}

export const BK_AVAILABILITY_TYPES: BkAvailabilityTypeDef[] = [
  // Seit 23.10.2026 die einzige neu anlegbare Art: „Abwesenheit" (ohne Art-Auswahl).
  { type: 'abwesenheit',  grund: 'Abwesenheit',  labelKey: 'betreuung.availability.type.abwesenheit',  color: '#e8792b' },
  { type: 'verfuegbar',   grund: 'Verfügbar',    labelKey: 'betreuung.availability.type.verfuegbar',   color: '#16a34a' },
  { type: 'urlaub',       grund: 'Urlaub',       labelKey: 'betreuung.availability.type.urlaub',       color: '#7c3aed' },
  { type: 'kein_kontakt', grund: 'Kein Kontakt', labelKey: 'betreuung.availability.type.kein_kontakt', color: '#b42318' },
];

/** Fester Grund, den jede neu angelegte BK-Abwesenheit in `absences.grund` erhält. */
export const BK_ABSENCE_GRUND = 'Abwesenheit';

/** Filterbare Arten: nur noch „Abwesenheit" (Altbestand Urlaub zählt dazu, siehe isAbsenceType). */
export const BK_AVAILABILITY_SELECTABLE: BkAvailabilityTypeDef[] =
  BK_AVAILABILITY_TYPES.filter(d => d.type === 'abwesenheit');

/** Zählt als Abwesenheit: neue „Abwesenheit" und der Altbestand „Urlaub". */
export function isAbsenceType(type: string | null | undefined): boolean {
  return type === 'abwesenheit' || type === 'urlaub';
}

/** Typ-Definition zu einem gespeicherten Grund (oder undefined). */
export function bkAvailabilityByGrund(grund: string | null | undefined): BkAvailabilityTypeDef | undefined {
  const g = (grund ?? '').trim();
  return BK_AVAILABILITY_TYPES.find(d => d.grund === g);
}

export function bkAvailabilityByType(type: string | null | undefined): BkAvailabilityTypeDef | undefined {
  return BK_AVAILABILITY_TYPES.find(d => d.type === type);
}

export interface BkAvailabilityEntry {
  id: number;
  user_id: number;
  type: BkAvailabilityType | null;
  grund: string;
  /** YYYY-MM-DD */
  startdatum: string;
  /** YYYY-MM-DD oder null (offen). */
  enddatum: string | null;
  /** Läuft heute. */
  is_current: boolean;
  /** Vor- und Nachname der BK. */
  name: string | null;
  /** BK-Stammdaten in der Form der v1-Listen (für `v1UserToCaregiver`). */
  user: V1User | null;
}

export interface BkAvailabilityParams {
  type?: BkAvailabilityType;
  /** open = läuft noch oder beginnt erst (Default), current = läuft heute, all. */
  scope?: 'open' | 'current' | 'all';
  limit?: number;
}

export const v2BkAvailability = {
  /** GET /v2/admin/bk-availability */
  list: async (params: BkAvailabilityParams = {}): Promise<BkAvailabilityEntry[]> => {
    const res = await apiV2.get<{ data: BkAvailabilityEntry[] }>('/admin/bk-availability', { params });
    return res.data.data ?? [];
  },
};

export type BkAvailableFrom =
  | { kind: 'date'; date: string }  // YYYY-MM-DD, erster freier Tag
  | { kind: 'unknown' };            // offene Abwesenheit ohne Ende

/** YYYY-MM-DD + 1 Tag, rein kalendarisch (keine Zeitzonen-/UTC-Schnitte). */
export function nextDay(ymd: string): string {
  const [y, m, d] = ymd.slice(0, 10).split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + 1));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}

/**
 * „Verfügbar ab" aus den additiven Feldern der BK-Liste (GET /v2/admin/bk):
 * `last_absence_end` (Ende der letzten, auch vergangenen Abwesenheit) und
 * `has_open_absence`. Offene Abwesenheit = unbekannt, keine Abwesenheit = null (—).
 * Der Folgetag darf in der Vergangenheit liegen (15.08.–15.10.2026 → 16.10.2026).
 */
export function availableFromFromRow(
  lastAbsenceEnd: string | null | undefined,
  hasOpenAbsence: boolean | null | undefined,
): BkAvailableFrom | null {
  if (hasOpenAbsence) return { kind: 'unknown' };
  if (!lastAbsenceEnd) return null;
  return { kind: 'date', date: nextDay(lastAbsenceEnd) };
}

/**
 * „Verfügbar ab" je BK (aus den Dispo-Einträgen) = erster Tag nach dem Ende der letzten Abwesenheit
 * (15.08.–15.10.2026 → 16.10.2026). Es zählen nur Abwesenheiten (neue
 * „Abwesenheit" + Altbestand „Urlaub"), nicht der Alt-Eintrag „Verfügbar".
 * Eine offene Abwesenheit ohne Ende ergibt „unbekannt". Die API liefert nur
 * laufende/künftige Einträge (scope=open, Ende >= heute): der Tag liegt also
 * nie in der Vergangenheit; eine BK ohne solchen Eintrag hat keinen Wert.
 */
export function availableFromByUser(entries: BkAvailabilityEntry[]): Map<number, BkAvailableFrom> {
  const map = new Map<number, BkAvailableFrom>();
  for (const e of entries) {
    if (!isAbsenceType(e.type)) continue;
    const prev = map.get(e.user_id);
    if (prev?.kind === 'unknown') continue;
    if (e.enddatum == null) { map.set(e.user_id, { kind: 'unknown' }); continue; }
    const next = nextDay(e.enddatum);
    if (!prev || (prev.kind === 'date' && next > prev.date)) map.set(e.user_id, { kind: 'date', date: next });
  }
  return map;
}

/**
 * Maßgeblicher Eintrag je BK: der heute laufende, sonst der nächste.
 * (Die API liefert nach Startdatum aufsteigend.)
 */
export function primaryEntryByUser(entries: BkAvailabilityEntry[]): Map<number, BkAvailabilityEntry> {
  const map = new Map<number, BkAvailabilityEntry>();
  for (const e of entries) {
    const prev = map.get(e.user_id);
    if (!prev || (!prev.is_current && e.is_current)) map.set(e.user_id, e);
  }
  return map;
}
